import { configureStore } from "@reduxjs/toolkit";
import domready from "domready";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Provider, useDispatch } from "react-redux";
import { apiMiddleware } from "redux-api-middleware";
import { getAccessToken, getSettings, refreshAccessToken, updateSettings } from "./Settings";
import { loadPassengerTypes, loadStops, setDestination, setOrigin, setReturnDate } from "./Search/Actions";
import { createBookingDialogReducer } from "./DialogReducer";
import { useStateSelector, ISettings, IStop, BookingDialogStep } from "./Models";
import { Search } from "./Search/Search";
import { createS3SearchReducer } from "./Search/SearchReducer";
import { SelectJourneys } from "./Timetable/SelectJourneys";
import { createTimetableReducer } from "./Timetable/TimetableReducer";
import { TravelDetails } from "./TravelDetails/TravelDetails";
import { createTravelDetailsReducer } from "./TravelDetails/TravelDetailsReducer";
import { createApiCallReducer } from "./ApiCalls/ApiCallReducer";
import dayjs from "dayjs";
import { createPassengersAndPaymentReducer } from "./PassengersAndPayment/PassengersAndPaymentReducer";
import { PassengersAndPayment } from "./PassengersAndPayment/PassengersAndPayment";
import { Alert } from "react-bootstrap";
import { getJSONFromAttribute } from "./Shared/Utils";
import { SetStore } from "./ApiCalls/Utils";
import { trackPageView } from "../Shared/Tracking";
import zenscroll from "zenscroll";
import { subscribe } from "../Shared/CustomEvents";
import { showDialog } from "./Actions";
import { ChangeSeats } from "./ChangeSeats/ChangeSeats";

let containerElement: HTMLElement|null;

export const isTest = () => process.env.NODE_ENV !== 'production' && !window.location.search.includes("notest");

const App = () => {

	const state = useStateSelector(state => state.dialog);
	const searchState = useStateSelector(state => state.search);
	const step = state.step;
	const dispatch = useDispatch();
	const settings = getSettings();

	useEffect(() => {
		const bodyElement = document.querySelector("body");

		if (state.visible) {
			bodyElement?.classList.add("dialog-visible");
			if (containerElement)
				zenscroll.intoView(containerElement, 500);
		}
		else {
			bodyElement?.classList.remove("dialog-visible");
		}
	}, [ state.visible ])

	//Sätt origin och destination från defaultvärden EFTER att stops har laddats
	useEffect(() => {
		if (searchState.stops?.length > 0)
		{
			if (!searchState.origin && searchState.defaultFrom)
			{
				const origin = searchState.stops.find(x => x.code === searchState.defaultFrom);
				if (origin)
					dispatch(setOrigin(origin));
			}
			if (!searchState.origin && searchState.defaultFrom)
			{
				const destination = searchState.stops.find(x => x.code === searchState.defaultTo);
				if (destination)
					dispatch(setDestination(destination));
			}
		}
	}, [searchState.stops]);

	//Se till att return date aldrig är före departure date
	useEffect(() => {
		if (!searchState.isTwoWay || !searchState.departureDate || !searchState.returnDate)
			return;

		if (dayjs(searchState.returnDate).isBefore(searchState.departureDate, "day"))
			dispatch(setReturnDate(searchState.departureDate));

	}, [searchState.departureDate]);

	//Se till att bokningsdialogen syns hela tiden den är öppen
	useEffect(() => {
		if (state.step && state.visible && document.getElementById("s3-booking-container"))
			zenscroll.intoView(document.getElementById("s3-booking-container"));
	}, [state.step]);

	//Tracka virtuella page views
	useEffect(() => {
		if (step)
			trackPageView(`/s3booking/${step}`, `Booking ${step}`);
	}, [step]);

	if (!state.visible)
		return null;


	return <div className="dialog-background">
		<React.StrictMode>
			{settings.token && !settings.token.accessToken && <Alert variant="warning"><strong>API not available.</strong> This is hopefully a temporary error.</Alert>}

			<div className="reservation-dialog container" id="s3-booking-container">
				<ShowStep step={step} />
			</div>
		</React.StrictMode>
	</div>
};

const ShowStep = (props: { step: BookingDialogStep }) => {
	switch(props.step) {
		case "search": return <Search />;
		case "timetable": return <SelectJourneys /> ;
		case "outbound": return <TravelDetails direction="outbound" />;
		case "inbound": return <TravelDetails direction="inbound" />;
		case "passengersandpayment": return <PassengersAndPayment />;
		case "seatmap": return <ChangeSeats />;
		default: return null;
	}
}

domready(() => {
	containerElement = document.getElementById("booking-app");

	if (!containerElement)
		return;

	const searchParams = new URLSearchParams(window?.location.search);
	const showBooking = searchParams.get("showBooking") ? true : false;
	const from = searchParams.get("from");
	const to = searchParams.get("to");
	const single = searchParams.get("single");
	let date = searchParams.get("date");
	let rdate = searchParams.get("rdate");

	const settings = getJSONFromAttribute<ISettings>(containerElement, "data-settings", undefined);
	updateSettings(settings);


	//Sätt defaultdatum till idag och imorgon (såvida man inte skickat in datum som parametrar)
	if (!date)
		date = dayjs().format("YYYY-MM-DD");
	if (!rdate)
		rdate = dayjs(date).add(1, "day").format("YYYY-MM-DD");

	const visible = showBooking || from || to ? true : false;

	const store = configureStore({
		reducer: {
			dialog: createBookingDialogReducer(visible),
			search: createS3SearchReducer({ defaultFrom: from, defaultTo: to, departureDate: date, returnDate: rdate, isTwoWay: single == null }),
			timetable: createTimetableReducer(),
			travelDetails: createTravelDetailsReducer(),
			passengersAndPayment: createPassengersAndPaymentReducer(),
			apicalls: createApiCallReducer()
		},
		middleware: (getDefaultMiddleware) => [apiMiddleware, ...getDefaultMiddleware({ serializableCheck: true })],
		devTools: {name: "S3Booking"}
	});

	SetStore(store);

	//Ladda initial data
	setTimeout(async () => {
		await getAccessToken();

		store.dispatch(loadPassengerTypes());
		store.dispatch(loadStops());
	}, 0);

	//Förnya token regelbundet
	setInterval(() => {
		refreshAccessToken();
	}, 15 * 60 * 1000); //token är giltig i 30 min, så vi förnyar den var 15 minut.

	subscribe("Search", event => {
		let search = event.detail || {} as { origin?: IStop, destination?: IStop }

		store.dispatch(showDialog(search));
	});

	const root = createRoot(containerElement);
	root.render(
		<Provider store={store}>
			<App />
		</Provider>);
});

if (isTest()) {
	console.debug("not production mode");
}